/* =============================================================================
   Monolith — filters.js
   Collection / search results controller:
   - Ajax filtering & sorting through the Section Rendering API (native Shopify
     storefront filtering — no client-side fake filtering)
   - Ajax pagination + browser history
   - Grid density toggle (persisted per viewport in localStorage, UI-only)
   Works without JS: the facets form is a plain GET form.
   ============================================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'monolith:grid-cols';

  class CollectionController {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.baseUrl = section.dataset.baseUrl || window.location.pathname;
      this.root = section.querySelector('[data-collection-root]');
      this.form = section.querySelector('[data-facets-form]');
      this.abortController = null;

      this.bind();
      this.applyStoredGrid();
      window.addEventListener('popstate', () => this.render(window.location.search, false));
    }

    bind() {
      // Facets form (sort + filters)
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.render(this.buildQuery(), true);
          const drawer = theme.drawers[`filters-${this.sectionId}`];
          if (drawer) drawer.close();
        });
        // Live result count preview on change (debounced) — keeps drawer open
        this.form.addEventListener(
          'change',
          theme.debounce(() => this.previewCount(), 250)
        );
        this.form.querySelectorAll('[data-facets-clear]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.render(this.extractQuery(link.getAttribute('href')), true);
          });
        });
      }

      // Delegated clicks inside the results root: active filter chips, pagination, grid toggle
      this.section.addEventListener('click', (event) => {
        const removeLink = event.target.closest('[data-facets-remove]');
        if (removeLink) {
          event.preventDefault();
          this.render(this.extractQuery(removeLink.getAttribute('href')), true);
          return;
        }
        const pageLink = event.target.closest('[data-pagination] a');
        if (pageLink) {
          event.preventDefault();
          this.render(this.extractQuery(pageLink.getAttribute('href')), true, true);
          return;
        }
        const gridBtn = event.target.closest('[data-grid-cols]');
        if (gridBtn) {
          event.preventDefault();
          this.setGrid(gridBtn);
        }
      });
    }

    /* ---------------------------------------------------------- URL helpers */
    buildQuery() {
      const params = new URLSearchParams();
      const formData = new FormData(this.form);
      for (const [key, value] of formData.entries()) {
        if (value === '' || value === null) continue;
        params.append(key, value);
      }
      return params.toString();
    }

    extractQuery(href) {
      try {
        const url = new URL(href, window.location.origin);
        return url.search.replace(/^\?/, '');
      } catch (e) {
        return '';
      }
    }

    /* ---------------------------------------------------------- Rendering */
    async render(query, pushHistory, scrollToTop = true) {
      if (!this.root) return;
      const cleanQuery = (query || '').replace(/^\?/, '');
      const url = `${this.baseUrl}${cleanQuery ? `?${cleanQuery}` : ''}`;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.root.classList.add('is-loading');
      this.root.setAttribute('aria-busy', 'true');

      try {
        const fetchUrl = `${url}${cleanQuery ? '&' : '?'}section_id=${this.sectionId}`;
        const response = await fetch(fetchUrl, { signal: this.abortController.signal });
        const html = await response.text();
        const doc = theme.parseHTML(html);

        const newRoot = doc.querySelector('[data-collection-root]');
        if (newRoot) this.root.innerHTML = newRoot.innerHTML;

        const newForm = doc.querySelector('[data-facets-form]');
        if (newForm && this.form) {
          // Replace drawer body + footer, keep the form element itself (listeners stay bound)
          const bodies = [['.facets-drawer__body'], ['.facets-drawer__footer']];
          bodies.forEach(([selector]) => {
            const from = newForm.querySelector(selector);
            const to = this.form.querySelector(selector);
            if (from && to) to.innerHTML = from.innerHTML;
          });
          // Header reset link
          const newHeader = doc.querySelector('.facets-drawer .drawer__header');
          const header = this.section.querySelector('.facets-drawer .drawer__header');
          if (newHeader && header) {
            header.innerHTML = newHeader.innerHTML;
            header.querySelectorAll('[data-drawer-close]').forEach((btn) =>
              btn.addEventListener('click', () => theme.drawers[`filters-${this.sectionId}`]?.close())
            );
            header.querySelectorAll('[data-facets-clear]').forEach((link) =>
              link.addEventListener('click', (event) => {
                event.preventDefault();
                this.render(this.extractQuery(link.getAttribute('href')), true);
              })
            );
          }
        }

        if (pushHistory) history.pushState({ collectionQuery: cleanQuery }, '', url);
        this.applyStoredGrid();

        if (scrollToTop) {
          const top = this.root.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--site-header-height'), 10) || 80) - 8;
          if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
        }
        theme.events.emit('collection:rendered', { section: this.section });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[theme] collection render failed', error);
          window.location.href = url; // graceful fallback: full navigation
        }
      } finally {
        this.root.classList.remove('is-loading');
        this.root.removeAttribute('aria-busy');
      }
    }

    /** Fetch only the result count to update the Apply button while the drawer is open. */
    async previewCount() {
      const countEl = this.form.querySelector('[data-facets-result-count]');
      if (!countEl) return;
      const query = this.buildQuery();
      try {
        const response = await fetch(`${this.baseUrl}?${query}&section_id=${this.sectionId}`);
        const doc = theme.parseHTML(await response.text());
        const count = doc.querySelector('[data-facets-result-count]');
        if (count) countEl.textContent = count.textContent;
      } catch (e) {
        /* ignore preview failures */
      }
    }

    /* ---------------------------------------------------------- Grid density */
    viewportKey() {
      return window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
    }

    setGrid(button) {
      const cols = button.dataset.gridCols;
      const toggle = button.closest('[data-grid-toggle]');
      const viewport = toggle ? toggle.dataset.gridToggle : this.viewportKey();
      this.applyGrid(viewport, cols);
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        stored[viewport] = cols;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (e) {
        /* storage unavailable */
      }
    }

    applyGrid(viewport, cols) {
      const grid = this.section.querySelector('[data-collection-grid]');
      if (grid) {
        if (viewport === 'desktop') {
          grid.style.setProperty('--grid-cols-desktop', cols);
          grid.style.setProperty('--grid-cols-tablet', Math.min(parseInt(cols, 10), 3));
        } else {
          grid.style.setProperty('--grid-cols-mobile', cols);
        }
      }
      const toggle = this.section.querySelector(`[data-grid-toggle="${viewport}"]`);
      if (toggle) {
        toggle.querySelectorAll('[data-grid-cols]').forEach((btn) => btn.classList.toggle('is-active', btn.dataset.gridCols === String(cols)));
      }
    }

    applyStoredGrid() {
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (stored.desktop) this.applyGrid('desktop', stored.desktop);
        if (stored.mobile) this.applyGrid('mobile', stored.mobile);
      } catch (e) {
        /* ignore */
      }
    }
  }

  function init(root = document) {
    root.querySelectorAll('[data-collection-section]').forEach((section) => {
      if (section.__collection) return;
      section.__collection = new CollectionController(section);
    });
  }

  theme.onDocumentReady(() => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
